// Single source of truth for the /updates changelog page.
//
// To log a new update: add an entry to `RELEASES` below. Keep macOS and Windows
// entries separate (each has its own `platform`), even for the same version
// number — the two apps ship independently. Newest first within each platform.
//
// Entry shape:
//   {
//     version:  '1.7.0',
//     platform: 'macos' | 'windows',
//     status:   'building' | 'shipped',
//     date:     'June 2026'            // human label; omit while building
//     summary:  'One-line headline for the release.',
//     link:     'https://...'          // optional — release notes / store / PR
//     changes:  [{ type, text }]       // type: feat | fix | perf | style | security
//   }

export const PLATFORMS = {
  macos: { id: 'macos', label: 'macOS' },
  windows: { id: 'windows', label: 'Windows' },
}

export const CHANGE_TYPES = {
  feat: { label: 'New' },
  fix: { label: 'Fix' },
  perf: { label: 'Performance' },
  style: { label: 'Visual' },
  security: { label: 'Security' },
}

export const RELEASES = [
  // ---------- macOS ----------
  {
    version: '1.7.0',
    platform: 'macos',
    status: 'building',
    summary: 'Custom sounds, a richer visualizer, deeper customization, and two new switches.',
    changes: [
      { type: 'feat', text: 'Custom sounds: import your own audio for the Enter key and mouse clicks, as one sound or separate press and release files.' },
      { type: 'feat', text: '“Your Keyboard” per-key configurator: set switch, tone, and volume per key.' },
      { type: 'feat', text: 'Bezel visualizer with a gooey metaball merge effect.' },
      { type: 'feat', text: 'Combo Counter themes with their own outline, fill, and glow.' },
      { type: 'feat', text: 'Keystrokes visualizer now flows by column or row direction, and its Scale slider resizes the keycaps.' },
      { type: 'feat', text: 'New switches: Aflion Carrot and Akko Clicky Pink.' },
      { type: 'perf', text: 'Keystroke animations now run at your display’s full refresh rate, silky on 120Hz ProMotion.' },
      { type: 'style', text: 'Cleaner Combo Counter: crisp white fill and outline, no glow.' },
      { type: 'fix', text: 'Keyboard sounds now recover cleanly when Input Monitoring permission is revoked and re-granted.' },
    ],
  },
  {
    version: '1.6.0',
    platform: 'macos',
    status: 'shipped',
    date: 'June 2026',
    summary: 'Keystroke visualizer redesign and spatial audio polish.',
    changes: [
      { type: 'style', text: 'Redesigned keystroke visualizer with keycap artwork.' },
      { type: 'feat', text: 'Spatial audio “brain massage” width slider.' },
      { type: 'perf', text: 'Vectorized decorrelation convolution so switch changes never stutter.' },
    ],
  },

  // ---------- Windows ----------
  {
    version: '1.8.1',
    platform: 'windows',
    status: 'shipped',
    date: 'August 2026',
    summary: 'Notch overlay controls now live in Settings.',
    changes: [
      { type: 'fix', text: 'The Notch Overlay and Auto-Hide toggles now appear in Settings under Visualizer, so they are easy to find. They remain in the tray menu too.' },
    ],
  },
  {
    version: '1.8.0',
    platform: 'windows',
    status: 'shipped',
    date: 'August 2026',
    summary: 'Notch overlay auto-hide and a Start menu launch fix.',
    changes: [
      { type: 'feat', text: 'Notch overlay auto-hide: it parks above the screen edge so it never covers your browser tabs, then slides down smoothly when you hover the top center of the screen.' },
      { type: 'fix', text: 'Opening Keeby from the Start menu while it was already running in the tray now brings up Settings instead of doing nothing.' },
    ],
  },
  {
    version: '1.7.0',
    platform: 'windows',
    status: 'shipped',
    date: 'July 2026',
    summary: 'Every Mac switch comes to Windows, plus license reliability fixes.',
    changes: [
      { type: 'feat', text: 'All Mac switches now on Windows: Aflion Carrot, Akko Clicky Pink, CS Jelly Black, and V3 Cream Yellow Pro, plus Lofree Flow 2 Surfer, Void, and Pulse.' },
      { type: 'style', text: 'Updated, more consistent switch sounds across every profile.' },
      { type: 'feat', text: 'A What’s New tab in Settings so you can see each release at a glance.' },
      { type: 'fix', text: 'Fixed the activation and re-validation failures that could lock out valid licenses.' },
      { type: 'fix', text: 'Activation errors now show a clear message instead of raw server text.' },
      { type: 'security', text: 'Server-signed license tokens, leak telemetry, and per-key revocation.' },
    ],
  },
  {
    version: '1.0.2',
    platform: 'windows',
    status: 'shipped',
    date: 'June 2026',
    summary: 'First public Windows build.',
    changes: [
      { type: 'feat', text: 'System-tray app with the notch visualizer and spatial audio engine.' },
      { type: 'fix', text: 'Corrected license validation for production keys.' },
    ],
  },
]

// Convenience: releases for one platform, building first, then newest shipped.
export function releasesFor(platformId) {
  const order = { building: 0, shipped: 1 }
  return RELEASES.filter((r) => r.platform === platformId).sort(
    (a, b) => (order[a.status] - order[b.status]) || b.version.localeCompare(a.version, undefined, { numeric: true }),
  )
}
